// pages/reports/TrialBalance.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { 
    ChevronUp, 
    RotateCcw, 
    Download,
    Printer,
    TrendingUp,
    TrendingDown,
    PieChart,
    FileText,
    List,
    File,
    CheckCircle,
    XCircle,
    AlertCircle,
    Filter,
    Search,
    RefreshCw,
    Layers,
    BookOpen,
    ChevronDown,
    
} from 'feather-icons-react/build/IconComponents';
import { trialBalanceActions } from '../../core/action/trialBalanceActions';

const MySwal = withReactContent(Swal);

const TrialBalance = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);
    
    // State
    const [trialBalance, setTrialBalance] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [toDate, setToDate] = useState(new Date());
    const [asOnDate, setAsOnDate] = useState(new Date());
    const [showZeroBalances, setShowZeroBalances] = useState(false);
    const [viewMode, setViewMode] = useState('detailed');
    const [expandedGroups, setExpandedGroups] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [groupFilter, setGroupFilter] = useState('all');
    const [sortBy, setSortBy] = useState('account_name');
    const [sortOrder, setSortOrder] = useState('asc');
    
    // Group options for filter
    const groupOptions = [
        { value: 'all', label: 'All Groups' },
        { value: 'Asset', label: 'Assets' },
        { value: 'Liability', label: 'Liabilities' },
        { value: 'Equity', label: 'Equity' },
        { value: 'Income', label: 'Income' },
        { value: 'Expense', label: 'Expenses' }
    ];
    
    // Sort options
    const sortOptions = [
        { value: 'account_name', label: 'Account Name' },
        { value: 'account_code', label: 'Account Code' },
        { value: 'opening_balance', label: 'Opening Balance' },
        { value: 'period_debits', label: 'Period Debits' },
        { value: 'period_credits', label: 'Period Credits' },
        { value: 'closing_balance', label: 'Closing Balance' }
    ];
    
    // Format date for API
    const formatDateForAPI = (date) => {
        return date.toISOString().split('T')[0];
    };
    
    // Load trial balance data
    const loadTrialBalance = useCallback(async () => {
        try {
            setLoading(true);
            
            const params = {
                from_date: formatDateForAPI(fromDate),
                to_date: formatDateForAPI(toDate),
                as_on_date: formatDateForAPI(asOnDate),
                show_zero: showZeroBalances,
                account_type: groupFilter !== 'all' ? groupFilter : ''
            };
            
            console.log('Loading trial balance with params:', params);
            
            const result = await trialBalanceActions.getTrialBalance(params);
            
            console.log('Trial Balance Result:', result);
            
            if (result.success) {
                setTrialBalance(result.data);
                
                // Initialize expanded groups
                const expanded = {};
                if (result.data.groups && result.data.groups.length > 0) {
                    result.data.groups.forEach(group => {
                        expanded[group.group] = true;
                    });
                }
                setExpandedGroups(expanded);
            } else {
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: result.message || 'Failed to load trial balance',
                    timer: 2000,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            console.error('Error loading trial balance:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load trial balance',
                timer: 2000,
                showConfirmButton: false
            });
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate, asOnDate, showZeroBalances, groupFilter]);
    
    // Load on mount and when dependencies change
    useEffect(() => {
        loadTrialBalance();
    }, [loadTrialBalance]);
    
    // Toggle group expansion
    const toggleGroup = (group) => {
        setExpandedGroups(prev => ({
            ...prev,
            [group]: !prev[group]
        }));
    };
    
    // Expand all groups
    const expandAllGroups = () => {
        if (!trialBalance || !trialBalance.groups) return;
        
        const expanded = {};
        trialBalance.groups.forEach(group => {
            expanded[group.group] = true;
        });
        setExpandedGroups(expanded);
    };
    
    // Collapse all groups
    const collapseAllGroups = () => {
        setExpandedGroups({});
    };
    
    // Handle date changes
    const handleFromDateChange = (date) => {
        setFromDate(date);
    };
    
    const handleToDateChange = (date) => {
        setToDate(date);
    };
    
    const handleAsOnDateChange = (date) => {
        setAsOnDate(date);
    };
    
    // Toggle zero balances
    const handleToggleZeroBalances = () => {
        setShowZeroBalances(prev => !prev);
    };
    
    // Handle refresh
    const handleRefresh = () => {
        loadTrialBalance();
    };
    
    // Handle export PDF
    const handleExportPDF = async () => {
        try {
            const params = {
                from_date: formatDateForAPI(fromDate),
                to_date: formatDateForAPI(toDate),
                as_on_date: formatDateForAPI(asOnDate),
                show_zero: showZeroBalances,
                view_mode: viewMode
            };
            
            const result = await trialBalanceActions.exportToPDF(params);
            
            if (result) {
                const url = window.URL.createObjectURL(new Blob([result.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `trial_balance_${formatDateForAPI(asOnDate)}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            console.error('Error exporting PDF:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to export PDF',
                timer: 2000,
                showConfirmButton: false
            });
        }
    };
    
    // Handle export Excel
    const handleExportExcel = async () => {
        try {
            const params = {
                from_date: formatDateForAPI(fromDate),
                to_date: formatDateForAPI(toDate),
                as_on_date: formatDateForAPI(asOnDate),
                show_zero: showZeroBalances,
                view_mode: viewMode
            };
            
            const result = await trialBalanceActions.exportToExcel(params);
            
            if (result) {
                const url = window.URL.createObjectURL(new Blob([result.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `trial_balance_${formatDateForAPI(asOnDate)}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            console.error('Error exporting Excel:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to export Excel',
                timer: 2000,
                showConfirmButton: false
            });
        }
    };
    
    // Handle print
    const handlePrint = () => {
        window.print();
    };
    
    // Handle sort
    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    };
    
    // Filter and sort ledgers
    const getFilteredAndSortedLedgers = () => {
        if (!trialBalance || !trialBalance.ledgers || trialBalance.ledgers.length === 0) return [];
        
        let filtered = trialBalance.ledgers.filter(ledger => {
            const matchesSearch = searchTerm === '' || 
                (ledger.account_name && ledger.account_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (ledger.account_code && ledger.account_code.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (ledger.account_category && ledger.account_category.toLowerCase().includes(searchTerm.toLowerCase()));
            
            const matchesGroup = groupFilter === 'all' || ledger.account_group === groupFilter;
            
            return matchesSearch && matchesGroup;
        });
        
        // Sort
        filtered.sort((a, b) => {
            let aVal = a[sortBy] !== undefined ? a[sortBy] : '';
            let bVal = b[sortBy] !== undefined ? b[sortBy] : '';
            
            if (typeof aVal === 'string') {
                aVal = aVal.toLowerCase();
                bVal = bVal.toLowerCase();
            }
            
            if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
        
        return filtered;
    };
    
    // Group ledgers by account group
    const getGroupedLedgers = () => {
        const filtered = getFilteredAndSortedLedgers();
        const grouped = {};
        
        filtered.forEach(ledger => {
            const group = ledger.account_group;
            if (!grouped[group]) {
                grouped[group] = {
                    group: group,
                    ledgers: [],
                    total_debits: 0,
                    total_credits: 0,
                    total_opening: 0,
                    total_closing: 0
                };
            }
            grouped[group].ledgers.push(ledger);
            grouped[group].total_debits += parseFloat(ledger.period_debits) || 0;
            grouped[group].total_credits += parseFloat(ledger.period_credits) || 0;
            grouped[group].total_opening += parseFloat(ledger.opening_balance) || 0;
            grouped[group].total_closing += parseFloat(ledger.closing_balance) || 0;
        });
        
        return grouped;
    };
    
    // Render summary cards
    const renderSummaryCards = () => {
        if (!trialBalance) return null;
        
        const { summary } = trialBalance;
        
        return (
            <div className="row mb-4">
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Ledgers</p>
                                    <h3 className="mb-0 text-primary">
                                        {summary.total_ledgers || 0}
                                    </h3>
                                    <small className="text-muted">Active Accounts</small>
                                </div>
                                <div className="bg-primary bg-opacity-10 rounded-circle p-3">
                                    <BookOpen size={24} className="text-primary" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Period Debits</p>
                                    <h3 className="mb-0 text-danger">
                                        {trialBalanceActions.formatCurrency(summary.total_period_debits || 0)}
                                    </h3>
                                    <small className="text-muted">This period</small>
                                </div>
                                <div className="bg-danger bg-opacity-10 rounded-circle p-3">
                                    <TrendingDown size={24} className="text-danger" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Period Credits</p>
                                    <h3 className="mb-0 text-success">
                                        {trialBalanceActions.formatCurrency(summary.total_period_credits || 0)}
                                    </h3>
                                    <small className="text-muted">This period</small>
                                </div>
                                <div className="bg-success bg-opacity-10 rounded-circle p-3">
                                    <TrendingUp size={24} className="text-success" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Trial Balance</p>
                                    <h3 className="mb-0 text-info">
                                        {trialBalanceActions.formatCurrency(summary.total_closing_debits || 0)}
                                    </h3>
                                    <small className="text-muted">
                                        {summary.is_balanced ? (
                                            <span className="text-success">
                                                <CheckCircle size={12} /> Balanced
                                            </span>
                                        ) : (
                                            <span className="text-danger">
                                                <AlertCircle size={12} /> Diff: {trialBalanceActions.formatCurrency(summary.difference || 0)}
                                            </span>
                                        )}
                                    </small>
                                </div>
                                <div className="bg-info bg-opacity-10 rounded-circle p-3">
                                    <PieChart size={24} className="text-info" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render period info
    const renderPeriodInfo = () => {
        if (!trialBalance) return null;
        
        const { period } = trialBalance;
        
        return (
            <div className="row mb-3">
                <div className="col-md-12">
                    <div className="card bg-light">
                        <div className="card-body py-2">
                            <div className="d-flex justify-content-between align-items-center flex-wrap">
                                <div>
                                    <strong>Period:</strong> {
                                        period?.from_date ? new Date(period.from_date).toLocaleDateString('en-IN', {
                                            day: '2-digit', month: 'short', year: 'numeric'
                                        }) : 'N/A'
                                    } to {
                                        period?.to_date ? new Date(period.to_date).toLocaleDateString('en-IN', {
                                            day: '2-digit', month: 'short', year: 'numeric'
                                        }) : 'N/A'
                                    }
                                </div>
                                <div>
                                    <strong>As on:</strong> {
                                        period?.as_on_date ? new Date(period.as_on_date).toLocaleDateString('en-IN', {
                                            day: '2-digit', month: 'long', year: 'numeric'
                                        }) : 'N/A'
                                    }
                                </div>
                                <div>
                                    <strong>View:</strong> {
                                        viewMode === 'summary' ? 'Summary' :
                                        viewMode === 'grouped' ? 'Grouped' : 'Detailed'
                                    }
                                </div>
                                <div>
                                    <strong>Zero Balances:</strong> {showZeroBalances ? 'Shown' : 'Hidden'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render verification row
    const renderVerificationRow = () => {
        if (!trialBalance) return null;
        
        const { summary } = trialBalance;
        const isBalanced = summary?.is_balanced;
        
        return (
            <div className={`alert ${isBalanced ? 'alert-success' : 'alert-danger'} mb-4`}>
                <div className="d-flex justify-content-between align-items-center flex-wrap">
                    <div>
                        <strong>
                            {isBalanced ? <CheckCircle size={18} className="me-2" /> : <XCircle size={18} className="me-2" />}
                            Trial Balance {isBalanced ? 'is Balanced' : 'is NOT Balanced'}
                        </strong>
                    </div>
                    <div className="d-flex flex-wrap">
                        <div className="me-4">
                            <small>Total Debits:</small>
                            <strong className="ms-2 text-danger">
                                {trialBalanceActions.formatCurrency(summary?.total_closing_debits || 0)}
                            </strong>
                        </div>
                        <div className="me-4">
                            <small>Total Credits:</small>
                            <strong className="ms-2 text-success">
                                {trialBalanceActions.formatCurrency(summary?.total_closing_credits || 0)}
                            </strong>
                        </div>
                        {!isBalanced && (
                            <div>
                                <small>Difference:</small>
                                <strong className="ms-2 text-warning">
                                    {trialBalanceActions.formatCurrency(summary?.difference || 0)}
                                </strong>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };
    
    // Render group summary
    const renderGroupSummary = () => {
        if (!trialBalance || !trialBalance.groups || trialBalance.groups.length === 0) {
            return (
                <div className="alert alert-info">
                    No group data available.
                </div>
            );
        }
        
        return (
            <div className="row mb-4">
                {trialBalance.groups.map((group, idx) => (
                    <div className="col-md-4 col-sm-6 mb-3" key={idx}>
                        <div className="card h-100">
                            <div className={`card-header ${trialBalanceActions.getGroupBadgeClass(group.group)} text-white py-2`}>
                                <h6 className="mb-0">{group.group} ({group.ledger_count || 0})</h6>
                            </div>
                            <div className="card-body py-2">
                                <div className="d-flex justify-content-between small">
                                    <span>Debits:</span>
                                    <span className="text-danger">
                                        {trialBalanceActions.formatCurrency(group.total_debits || 0)}
                                    </span>
                                </div>
                                <div className="d-flex justify-content-between small">
                                    <span>Credits:</span>
                                    <span className="text-success">
                                        {trialBalanceActions.formatCurrency(group.total_credits || 0)}
                                    </span>
                                </div>
                                <hr className="my-1" />
                                <div className="d-flex justify-content-between">
                                    <strong>Balance:</strong>
                                    <strong className={(group.total_balance || 0) >= 0 ? 'text-success' : 'text-danger'}>
                                        {trialBalanceActions.formatCurrency(group.total_balance || 0)}
                                    </strong>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };
    
    // Render sort indicator
    const renderSortIndicator = (field) => {
        if (sortBy !== field) return null;
        return sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
    };
    
    // Render detailed table
    const renderDetailedTable = () => {
        const filteredLedgers = getFilteredAndSortedLedgers();
        
        if (filteredLedgers.length === 0) {
            return (
                <div className="alert alert-info">
                    No ledgers found matching your criteria.
                </div>
            );
        }
        
        return (
            <div className="table-responsive">
                <table className="table table-hover table-bordered">
                    <thead className="table-light">
                        <tr>
                            <th onClick={() => handleSort('account_name')} style={{ cursor: 'pointer' }}>
                                Account Name {renderSortIndicator('account_name')}
                            </th>
                            <th onClick={() => handleSort('account_code')} style={{ cursor: 'pointer' }}>
                                Code {renderSortIndicator('account_code')}
                            </th>
                            <th>Group</th>
                            <th>Category</th>
                            <th className="text-end" onClick={() => handleSort('opening_balance')} style={{ cursor: 'pointer' }}>
                                Opening Balance {renderSortIndicator('opening_balance')}
                            </th>
                            <th className="text-end" onClick={() => handleSort('period_debits')} style={{ cursor: 'pointer' }}>
                                Period Debits {renderSortIndicator('period_debits')}
                            </th>
                            <th className="text-end" onClick={() => handleSort('period_credits')} style={{ cursor: 'pointer' }}>
                                Period Credits {renderSortIndicator('period_credits')}
                            </th>
                            <th className="text-end" onClick={() => handleSort('closing_balance')} style={{ cursor: 'pointer' }}>
                                Closing Balance {renderSortIndicator('closing_balance')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredLedgers.map((ledger, idx) => {
                            const openingType = trialBalanceActions.getBalanceType(ledger.opening_balance, ledger.account_group);
                            const closingType = trialBalanceActions.getBalanceType(ledger.closing_balance, ledger.account_group);
                            
                            return (
                                <tr key={idx}>
                                    <td>
                                        <strong>{ledger.account_name || 'N/A'}</strong>
                                        <br />
                                        <small className="text-muted">{ledger.ledger_type || 'N/A'}</small>
                                    </td>
                                    <td>{ledger.account_code || '-'}</td>
                                    <td>
                                        <span className={`badge ${trialBalanceActions.getGroupBadgeClass(ledger.account_group)}`}>
                                            {ledger.account_group || 'N/A'}
                                        </span>
                                    </td>
                                    <td>{ledger.account_category || 'N/A'}</td>
                                    <td className="text-end">
                                        {trialBalanceActions.formatCurrency(ledger.opening_balance || 0)}
                                        {openingType && <small className="text-muted ms-1">({openingType})</small>}
                                    </td>
                                    <td className="text-end text-danger">
                                        {trialBalanceActions.formatCurrency(ledger.period_debits || 0)}
                                    </td>
                                    <td className="text-end text-success">
                                        {trialBalanceActions.formatCurrency(ledger.period_credits || 0)}
                                    </td>
                                    <td className="text-end">
                                        <strong className={(ledger.closing_balance || 0) >= 0 ? 'text-success' : 'text-danger'}>
                                            {trialBalanceActions.formatCurrency(ledger.closing_balance || 0)}
                                        </strong>
                                        {closingType && <small className="text-muted ms-1">({closingType})</small>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot className="table-primary">
                        <tr>
                            <th colSpan="4" className="text-end">Totals:</th>
                            <th className="text-end">
                                {trialBalanceActions.formatCurrency(trialBalance.summary?.total_opening_debits || 0)} Dr / {' '}
                                {trialBalanceActions.formatCurrency(trialBalance.summary?.total_opening_credits || 0)} Cr
                            </th>
                            <th className="text-end text-danger">
                                {trialBalanceActions.formatCurrency(trialBalance.summary?.total_period_debits || 0)}
                            </th>
                            <th className="text-end text-success">
                                {trialBalanceActions.formatCurrency(trialBalance.summary?.total_period_credits || 0)}
                            </th>
                            <th className="text-end">
                                {trialBalanceActions.formatCurrency(trialBalance.summary?.total_closing_debits || 0)} Dr / {' '}
                                {trialBalanceActions.formatCurrency(trialBalance.summary?.total_closing_credits || 0)} Cr
                            </th>
                        </tr>
                    </tfoot>
                </table>
            </div>
        );
    };
    
    // Render grouped view
    const renderGroupedView = () => {
        const grouped = getGroupedLedgers();
        const groupOrder = ['Asset', 'Liability', 'Equity', 'Income', 'Expense'];
        
        if (Object.keys(grouped).length === 0) {
            return (
                <div className="alert alert-info">
                    No ledgers found matching your criteria.
                </div>
            );
        }
        
        return (
            <div>
                <div className="mb-3">
                    <button className="btn btn-sm btn-outline-primary me-2" onClick={expandAllGroups}>
                        <ChevronDown size={14} className="me-1" />
                        Expand All
                    </button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={collapseAllGroups}>
                        <ChevronUp size={14} className="me-1" />
                        Collapse All
                    </button>
                </div>
                
                {groupOrder.map(groupName => {
                    if (!grouped[groupName]) return null;
                    
                    const group = grouped[groupName];
                    const isExpanded = expandedGroups[groupName];
                    
                    return (
                        <div className="card mb-4" key={groupName}>
                            <div 
                                className={`card-header ${trialBalanceActions.getGroupBadgeClass(groupName)} text-white d-flex justify-content-between align-items-center`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => toggleGroup(groupName)}
                            >
                                <h5 className="mb-0">
                                    {groupName} ({group.ledgers.length})
                                    <span className="ms-3 small">
                                        Opening: {trialBalanceActions.formatCurrency(group.total_opening || 0)} | 
                                        Closing: {trialBalanceActions.formatCurrency(group.total_closing || 0)}
                                    </span>
                                </h5>
                                <div>
                                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                </div>
                            </div>
                            
                            {isExpanded && (
                                <div className="card-body">
                                    <div className="table-responsive">
                                        <table className="table table-sm table-hover">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Account Name</th>
                                                    <th>Code</th>
                                                    <th>Category</th>
                                                    <th className="text-end">Opening</th>
                                                    <th className="text-end">Debits</th>
                                                    <th className="text-end">Credits</th>
                                                    <th className="text-end">Closing</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {group.ledgers.map((ledger, idx) => {
                                                    const closingType = trialBalanceActions.getBalanceType(ledger.closing_balance, ledger.account_group);
                                                    
                                                    return (
                                                        <tr key={idx}>
                                                            <td>{ledger.account_name || 'N/A'}</td>
                                                            <td>{ledger.account_code || '-'}</td>
                                                            <td>{ledger.account_category || 'N/A'}</td>
                                                            <td className="text-end">
                                                                {trialBalanceActions.formatCurrency(ledger.opening_balance || 0)}
                                                            </td>
                                                            <td className="text-end text-danger">
                                                                {trialBalanceActions.formatCurrency(ledger.period_debits || 0)}
                                                            </td>
                                                            <td className="text-end text-success">
                                                                {trialBalanceActions.formatCurrency(ledger.period_credits || 0)}
                                                            </td>
                                                            <td className="text-end">
                                                                <strong>
                                                                    {trialBalanceActions.formatCurrency(ledger.closing_balance || 0)}
                                                                </strong>
                                                                {closingType && <small className="text-muted ms-1">({closingType})</small>}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                            <tfoot className="table-secondary">
                                                <tr>
                                                    <th colSpan="3" className="text-end">Group Total:</th>
                                                    <th className="text-end">
                                                        {trialBalanceActions.formatCurrency(group.total_opening || 0)}
                                                    </th>
                                                    <th className="text-end text-danger">
                                                        {trialBalanceActions.formatCurrency(group.total_debits || 0)}
                                                    </th>
                                                    <th className="text-end text-success">
                                                        {trialBalanceActions.formatCurrency(group.total_credits || 0)}
                                                    </th>
                                                    <th className="text-end">
                                                        {trialBalanceActions.formatCurrency(group.total_closing || 0)}
                                                    </th>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };
    
    // Render filter bar
    const renderFilterBar = () => {
        return (
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-3">
                            <label className="form-label">From Date</label>
                            <DatePicker
                                selected={fromDate}
                                onChange={handleFromDateChange}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                maxDate={toDate}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">To Date</label>
                            <DatePicker
                                selected={toDate}
                                onChange={handleToDateChange}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                minDate={fromDate}
                                maxDate={new Date()}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">As on Date</label>
                            <DatePicker
                                selected={asOnDate}
                                onChange={handleAsOnDateChange}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                maxDate={new Date()}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Account Group</label>
                            <Select
                                options={groupOptions}
                                value={groupOptions.find(opt => opt.value === groupFilter)}
                                onChange={(opt) => setGroupFilter(opt?.value || 'all')}
                                className="react-select"
                                classNamePrefix="select"
                            />
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="showZeroBalances"
                                    checked={showZeroBalances}
                                    onChange={handleToggleZeroBalances}
                                />
                                <label className="form-check-label" htmlFor="showZeroBalances">
                                    Show Zero Balances
                                </label>
                            </div>
                        </div>
                    </div>
                    
                    <div className="row mt-3">
                        <div className="col-md-4">
                            <div className="input-group">
                                <span className="input-group-text">
                                    <Search size={16} />
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by account name, code, or category..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searchTerm && (
                                    <button 
                                        className="btn btn-outline-secondary" 
                                        type="button"
                                        onClick={() => setSearchTerm('')}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="input-group">
                                <span className="input-group-text">
                                    <Filter size={16} />
                                </span>
                                <Select
                                    options={sortOptions}
                                    value={sortOptions.find(opt => opt.value === sortBy)}
                                    onChange={(opt) => setSortBy(opt?.value || 'account_name')}
                                    className="react-select flex-grow-1"
                                    classNamePrefix="select"
                                    placeholder="Sort by"
                                />
                                <button 
                                    className="btn btn-outline-secondary" 
                                    type="button"
                                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                                >
                                    {sortOrder === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                </button>
                            </div>
                        </div>
                        <div className="col-md-4 text-end">
                            <button className="btn btn-primary me-2" onClick={loadTrialBalance}>
                                <RefreshCw size={16} className="me-2" />
                                Apply Filters
                            </button>
                        </div>
                    </div>
                    
                    {/* Display current filter stats */}
                    {trialBalance && trialBalance.ledgers && (
                        <div className="row mt-3">
                            <div className="col-md-12">
                                <div className="alert alert-info py-2 mb-0">
                                    <small>
                                        <strong>Showing:</strong> {getFilteredAndSortedLedgers().length} of {trialBalance.ledgers.length} ledgers
                                        {groupFilter !== 'all' && ` | Group: ${groupFilter}`}
                                        {searchTerm && ` | Search: "${searchTerm}"`}
                                    </small>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };
    
    // View mode selector
    const renderViewModeSelector = () => {
        const viewOptions = [
            { value: 'summary', label: 'Summary', icon: <FileText size={16} /> },
            { value: 'grouped', label: 'Grouped', icon: <Layers size={16} /> },
            { value: 'detailed', label: 'Detailed', icon: <List size={16} /> }
        ];
        
        return (
            <div className="btn-group btn-group-sm me-2">
                {viewOptions.map(option => (
                    <button
                        key={option.value}
                        className={`btn ${viewMode === option.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setViewMode(option.value)}
                        title={option.label}
                    >
                        {option.icon}
                        <span className="ms-1 d-none d-md-inline">{option.label}</span>
                    </button>
                ))}
            </div>
        );
    };
    
    // Tooltip renderer
    const renderTooltip = useCallback((props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    ), []);
    
    if (loading && !trialBalance) {
        return (
            <div className="page-wrapper">
                <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <h5 className="text-muted">Loading Trial Balance...</h5>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="add-item d-flex">
                        <div className="page-title">
                            <h4>Trial Balance</h4>
                            <h6>Verify debits equal credits for a given period</h6>
                        </div>
                    </div>
                    
                    <ul className="table-top-head">
                        <li>
                            {renderViewModeSelector()}
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export PDF')}>
                                <button className="btn btn-outline-danger" onClick={handleExportPDF}>
                                    <File size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export Excel')}>
                                <button className="btn btn-outline-success" onClick={handleExportExcel}>
                                    <Download size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Print')}>
                                <button className="btn btn-outline-secondary" onClick={handlePrint}>
                                    <Printer size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <button 
                                    className="btn btn-outline-secondary" 
                                    onClick={handleRefresh}
                                    disabled={loading}
                                >
                                    <RotateCcw size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <button
                                    className={data ? "btn btn-outline-secondary active" : "btn btn-outline-secondary"}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>
                
                {/* Filter Bar */}
                {renderFilterBar()}
                
                {/* Period Info */}
                {renderPeriodInfo()}
                
                {/* Summary Cards */}
                {renderSummaryCards()}
                
                {/* Verification Row */}
                {renderVerificationRow()}
                
                {/* Group Summary (only in summary view) */}
                {viewMode === 'summary' && renderGroupSummary()}
                
                {/* Main Content */}
                {viewMode === 'grouped' ? (
                    renderGroupedView()
                ) : viewMode === 'detailed' ? (
                    renderDetailedTable()
                ) : viewMode === 'summary' ? (
                    <div className="alert alert-info">
                        Select a group to view details or switch to Detailed/Grouped view.
                    </div>
                ) : null}
                
                {/* Balance verification footer */}
                {trialBalance && (
                    <div className="card mt-3">
                        <div className="card-body text-center">
                            <p className="mb-0 text-muted">
                                <small>
                                    The trial balance proves the mathematical equality of debits and credits after posting.
                                    {trialBalance.summary?.is_balanced 
                                        ? ' All accounts are in balance.' 
                                        : ' There is a discrepancy that needs investigation.'}
                                </small>
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TrialBalance;